import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import userAuthService from './user-auth.service'
import type {
  UserLoginByPhoneInput,
  UserRefreshTokenInput,
  UserRegisterByPhoneInput,
  UserQuickKYCInput,
  UserLoginByEmailInput,
  UserRegisterByEmailInput,
  UserSetUserEmailInput,
  UserSetUserPhoneInput,
  UserCreateEmailOTPInput,
  UserResendOTPInput,
  UserCreateOTPInput,
  UserCheckUserOutput,
  UserCheckUserGetParams,
  UserGetAgentParams,
  UserGetAgentOutput,
  UserGetFeatureFlagsOutput,
  UserGetAgentByUsernameParams,
} from '../api'
import {
  featureFlagsOptions,
  getAgentByUsernameOptions,
  getAgentOptions,
  userAuthDetailsOptions,
} from './user-auth.options'
import { EUserAuthQueryKey } from './user-auth.types'
import type { IMutationArgs } from '../types'

export const useLoginByPhone = () => {
  return useMutation({
    mutationFn: (data: UserLoginByPhoneInput) =>
      userAuthService.loginByPhone(data),
  })
}

export const useLoginByEmail = () => {
  return useMutation({
    mutationFn: (data: UserLoginByEmailInput) =>
      userAuthService.loginByEmail(data),
  })
}

export const useRegisterByPhone = () => {
  return useMutation({
    mutationFn: (data: UserRegisterByPhoneInput) =>
      userAuthService.registerByPhone(data),
  })
}

export const useRegisterByEmail = () => {
  return useMutation({
    mutationFn: (data: UserRegisterByEmailInput) =>
      userAuthService.registerByEmail(data),
  })
}

export const useRefresh = () => {
  return useMutation({
    mutationFn: (data: UserRefreshTokenInput) => userAuthService.refresh(data),
  })
}

export const useVerify = () => {
  return useMutation({
    mutationFn: (data: UserQuickKYCInput) => userAuthService.verify(data),
  })
}

export const useSetUserEmail = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args
  return useMutation({
    mutationFn: (data: UserSetUserEmailInput) =>
      userAuthService.setUserEmail(data),
    onSuccess: (data) => {
      if (invalidate)
        queryClient.invalidateQueries({
          queryKey: [EUserAuthQueryKey.USER_AUTH_DETAILS],
        })
      return data
    },
  })
}

export const useSetUserPhone = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args
  return useMutation({
    mutationFn: (data: UserSetUserPhoneInput) =>
      userAuthService.setUserPhone(data),
    onSuccess: (data) => {
      if (invalidate)
        queryClient.invalidateQueries({
          queryKey: [EUserAuthQueryKey.USER_AUTH_DETAILS],
        })
      return data
    },
  })
}

export const usePrefetchUserAuthDetails = () => {
  return useSuspenseQuery(queryOptions(userAuthDetailsOptions()))
}

export const useUserAuthDetails = () => {
  return useQuery(userAuthDetailsOptions())
}

export const useEmailOtp = () => {
  return useMutation({
    mutationFn: (data: UserCreateEmailOTPInput) =>
      userAuthService.createEmailOtp(data),
  })
}

export const usePhoneOtp = () => {
  return useMutation({
    mutationFn: (data: UserCreateOTPInput) =>
      userAuthService.createPhoneOtp(data),
  })
}

export function useResendOtp() {
  return useMutation({
    mutationFn: (data: UserResendOTPInput) => userAuthService.resendOtp(data),
  })
}

export function useCheckUser(): UseMutationResult<
  UserCheckUserOutput,
  Error,
  UserCheckUserGetParams,
  unknown
> {
  return useMutation({
    mutationFn: (data: UserCheckUserGetParams) =>
      userAuthService.checkUser(data),
  })
}

export function useGetAgent(
  params: UserGetAgentParams,
  enabled: boolean,
): UseQueryResult<UserGetAgentOutput, Error> {
  return useQuery(getAgentOptions(params, enabled))
}

export function useGetAgentByUsername(
  params: UserGetAgentByUsernameParams,
  enabled: boolean,
): UseQueryResult<UserGetAgentOutput, Error> {
  return useQuery(getAgentByUsernameOptions(params, enabled))
}
export function useFeatureFlags(options?: {
  refetchOnWindowFocus?: boolean
  refetchOnMount?: boolean
  refetchOnReconnect?: boolean
  staleTime?: number
}): UseQueryResult<UserGetFeatureFlagsOutput, Error> {
  return useQuery(featureFlagsOptions(options))
}
